import { Box, Divider, Typography } from "@mui/material";

export const CustomTooltip = ({ active, payload, label, currency, isPercentage, theme }) => {
  if (!active || !payload || payload.length === 0) return null;

  // Check if there's a projected value in the payload data
  const firstPayload = payload[0]?.payload;
  const projectedKeys = firstPayload
    ? Object.keys(firstPayload).filter((k) => k.endsWith("_projected"))
    : [];
  const hasProjection = projectedKeys.length > 0;

  // Determine if this is the current month
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthNum = now.getMonth();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const isCurrentMonth =
    label &&
    label.includes(`${currentYear}`) &&
    (label.includes(`-${String(currentMonthNum + 1).padStart(2, "0")}`) ||
      label.includes(monthNames[currentMonthNum]));

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.accent,
        border: "1px solid #444",
        borderRadius: "6px",
        padding: "10px 12px",
        color: theme.palette.text.primary,
        fontSize: 12,
        minWidth: "160px",
        opacity: 0.9,
      }}
    >
      <Typography sx={{ fontWeight: "bold", fontSize: 13, textTransform: "uppercase" }}>
        {label}
      </Typography>

      <Divider sx={{ my: 1, borderColor: "#555" }} />

      {payload.map((entry, index) => {
        // Replace "value" with "Current" only for the current month, otherwise "Value"
        const displayName =
          entry.name === "value" ? (isCurrentMonth ? "Current" : "Value") : entry.name;

        return (
          <Box
            key={`item-${index}`}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ fontSize: 12, mt: 0.5 }}
          >
            <Box display="flex" alignItems="center" sx={{ mr: 5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  marginRight: 1,
                }}
              />
              <span>{displayName}</span>
            </Box>

            <span>
              {typeof entry.value === "number" && currency
                ? `$${entry.value.toLocaleString()}`
                : isPercentage
                  ? `${entry.value}%`
                  : entry.value}
            </span>
          </Box>
        );
      })}

      {/* Show projected value if it exists */}
      {hasProjection &&
        projectedKeys.map((projKey) => {
          const projValue = firstPayload[projKey];
          if (projValue) {
            return (
              <Box
                key={`projected-${projKey}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ fontSize: 12, mt: 0.5, borderTop: "1px dashed #555", pt: 0.5 }}
              >
                <Box display="flex" alignItems="center" sx={{ mr: 5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "#ff9800",
                      marginRight: 1,
                    }}
                  />
                  <span style={{ fontStyle: "italic" }}>Projected</span>
                </Box>

                <span style={{ color: "#ff9800", fontWeight: "bold" }}>
                  {typeof projValue === "number" && currency
                    ? `$${projValue.toLocaleString()}`
                    : isPercentage
                      ? `${projValue}%`
                      : projValue}
                </span>
              </Box>
            );
          }
          return null;
        })}
    </Box>
  );
};

export const hashKeyToColor = (key) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash * 137) % 360;
  const h = hue / 360,
    s = 0.7,
    l = 0.5;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };
  return `#${((1 << 24) + (f(0) << 16) + (f(8) << 8) + f(4)).toString(16).slice(1)}`;
};

export const adjustColorBrightness = (hex, amount) => {
  if (!hex.startsWith("#")) return hex;
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0xff) + amount;
  let b = (num & 0xff) + amount;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export const renderCustomLegend = (filteredData, selectedProvider, theme, finalColors) => {
  if (!filteredData || filteredData.length === 0 || !selectedProvider) return null;

  const allKeys = Object.keys(filteredData[0] || {}).filter((k) => k !== "month" && k !== "unit");

  if (selectedProvider !== "ALL") {
    return (
      <Box display="flex" justifyContent="center" mt={2} flexWrap="wrap">
        {allKeys.map((key) => {
          if (key === "unit") return null;

          const group = selectedProvider;
          const keysInGroup = allKeys.filter((k) => k.startsWith(group));
          const isSingleLine = keysInGroup.length === 1;
          const label = key === "value" ? selectedProvider : key;

          const color = isSingleLine
            ? hashKeyToColor(group)
            : adjustColorBrightness(hashKeyToColor(group), (20 * keysInGroup.indexOf(key)) % 60);

          return (
            <Box key={key} display="flex" alignItems="center" mr={1}>
              <Box width={12} height={12} mr={0.5} bgcolor={color} />
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 12,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {label.toUpperCase()}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  // For "ALL" view, use the same color logic as the line rendering
  return (
    <Box display="flex" justifyContent="center" mt={2} flexWrap="wrap">
      {allKeys.map((key) => {
        if (key === "unit") return null;

        // Match the EXACT logic from lineGraph.js line 434-441
        const group = key.split(" - ")[0];
        const keysInGroup = allKeys.filter((k) => k.startsWith(group));
        const isSingleLine = keysInGroup.length === 1;
        const colorBase = hashKeyToColor(group);
        const indexInGroup = keysInGroup.indexOf(key);
        const color = isSingleLine ? colorBase : getVariantColor(colorBase, indexInGroup);
        const label = key.includes(" - ") ? key.split(" - ").pop() : key;

        return (
          <Box key={key} display="flex" alignItems="center" mr={1}>
            <Box width={12} height={12} mr={0.5} bgcolor={color} />
            <Typography
              sx={{
                color: theme.palette.text.primary,
                fontSize: 12,
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            >
              {label.toUpperCase()}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export const getDistinctColor = (key, index = 0) => {
  const goldenAngle = 137.508;
  const hue = (index * goldenAngle) % 360;
  return `hsl(${hue}, 65%, 55%)`;
};

export const CustomXTick = ({ x, y, payload, theme }) => {
  if (!payload?.value) return null;
  const [labelTop, labelBottom] = payload.value.split(" ");
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fontWeight="bold"
        fill={theme.palette.text.primary}
      >
        {labelTop}
        <tspan x={0} dy={15} fontSize={12}>
          {labelBottom}
        </tspan>
      </text>
    </g>
  );
};

export const getVariantColor = (baseHex, index) => {
  const angleShift = (index + 1) * 12; // Shift hue for better contrast
  return adjustColorBrightness(rotateHue(baseHex, angleShift), (index + 1) * 32);
};

export const rotateHue = (hex, angle) => {
  let hsl = hexToHSL(hex);
  hsl.h = (hsl.h + angle) % 360;
  return HSLToHex(hsl.h, hsl.s, hsl.l);
};

export const hexToHSL = (H) => {
  // Convert hex to RGB first
  let r = 0,
    g = 0,
    b = 0;
  if (H.length === 4) {
    r = parseInt("0x" + H[1] + H[1]);
    g = parseInt("0x" + H[2] + H[2]);
    b = parseInt("0x" + H[3] + H[3]);
  } else if (H.length === 7) {
    r = parseInt("0x" + H[1] + H[2]);
    g = parseInt("0x" + H[3] + H[4]);
    b = parseInt("0x" + H[5] + H[6]);
  }

  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return { h, s, l };
};

export const HSLToHex = (h, s, l) => {
  l /= 1;
  let a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};
